import {
  Box,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { SectionProps } from "../../sections/sectionsComponents";
import { EventDetailSettlementReportSection } from "@common/interfaces/KYDEvent";
import Dinero from "dinero.js";
import EventDetailTitle from "../../../EventDetailTitle";
import { FaHourglassHalf, FaTag } from "react-icons/fa";
import DownloadReportsDropdown from "./DownloadReportsDropdown";

export const formatCurrency = (
  amount: number = 0,
  currency: Dinero.Currency = "USD"
) => {
  if (!currency) return "";

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  });

  const units = Dinero({
    amount,
    currency,
  }).toUnit();

  return formatter.format(units);
};

const SettlementReportSection = ({
  data,
  reports,
}: SectionProps<EventDetailSettlementReportSection["data"]>) => {
  if (!data?.settlement_report) {
    return null;
  }

  const totalTickets = data.settlement_report.total_by_source.reduce(
    (acc, curr) => acc + curr.sold,
    0
  );

  const totalValue = data.settlement_report.total_by_source.reduce(
    (acc, curr) => acc + curr.gross.amount,
    0
  );

  return (
    <VStack gap={8} w="full">
      <Stack
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        w="full"
      >
        <EventDetailTitle>Settlement Report</EventDetailTitle>
        <DownloadReportsDropdown reports={reports} />
      </Stack>

      {/* Total Summary Section */}
      <Stack direction={["column"]} flexGrow={1} w="full">
        <Stack
          justifyContent={["space-between"]}
          alignItems={["flex-start", "center"]}
          w="full"
          gap={[2, 8]}
          direction={["column", "row"]}
        >
          <VStack
            alignItems={"flex-start"}
            gap={0}
            h="full"
            flexGrow={1}
            w={["full", "60%"]}
          >
            <Text fontWeight={"semibold"}>Total Sales</Text>
          </VStack>
          <Stack
            direction={["row"]}
            justifyContent={["flex-end"]}
            w={["full", "40%"]}
            gap={4}
          >
            <VStack
              alignItems={["flex-start", "center"]}
              h="full"
              justifyItems={"flex-start"}
              gap={0}
              w="50%"
            >
              <Text
                color="gray.600"
                fontSize={"x-small"}
                letterSpacing={"wide"}
                textTransform={"uppercase"}
                fontWeight={"semibold"}
                textAlign={["start", "center"]}
              >
                Tickets
              </Text>
              <Text fontWeight={"semibold"} fontSize={"large"}>
                {totalTickets}
              </Text>
            </VStack>
            <VStack
              alignItems={"flex-end"}
              h="full"
              gap={0}
              justifyItems={"flex-start"}
              w="50%"
            >
              <Text
                color="gray.600"
                fontSize={"x-small"}
                letterSpacing={"wide"}
                textTransform={"uppercase"}
                fontWeight={"semibold"}
                textAlign={"end"}
              >
                Total face value
              </Text>
              <Text fontWeight={"semibold"} fontSize={"large"}>
                {formatCurrency(
                  totalValue,
                  data.settlement_report.total_by_source[0]?.gross.currency
                )}
              </Text>
            </VStack>
          </Stack>
        </Stack>
        <VStack
          flexGrow={1}
          alignItems={"flex-start"}
          fontSize={"small"}
          w="full"
          gap={[4]}
        >
          {data.settlement_report.total_by_source.map((source) => (
            <HStack
              w="full"
              justifyContent={"space-between"}
              gap={[2, 8]}
              key={source.name}
            >
              <VStack
                alignItems={"flex-start"}
                flexGrow={1}
                w={["full", "60%"]}
                gap={1}
              >
                <Text color="gray.600">{source.name}</Text>
              </VStack>
              <Stack
                direction={["row"]}
                justifyContent={["flex-end"]}
                w={["full", "40%"]}
                gap={4}
              >
                <VStack h="full" w="50%" alignItems={["flex-start", "center"]}>
                  <Text>{source.sold}</Text>
                </VStack>
                <VStack alignItems={"flex-end"} w="50%">
                  <Text>
                    {formatCurrency(source.gross.amount, source.gross.currency)}
                  </Text>
                </VStack>
              </Stack>
            </HStack>
          ))}
        </VStack>
      </Stack>

      {/* Divider between total and ticket types */}
      <Box w="full" h="1px" bg="gray.200" />

      {/* Ticket Types Section */}
      {data.settlement_report?.ticket_types.map((tt) => (
        <Stack direction={["column"]} flexGrow={1} w="full" key={tt.name}>
          <Stack
            justifyContent={["space-between"]}
            alignItems={["flex-start", "center"]}
            w="full"
            gap={[2, 8]}
            direction={["column", "row"]}
          >
            <VStack
              alignItems={"flex-start"}
              gap={0}
              h="full"
              flexGrow={1}
              w={["full", "60%"]}
            >
              <Text fontWeight={"semibold"}>{tt.name}</Text>
            </VStack>
            <Stack
              direction={["row"]}
              justifyContent={["flex-end"]}
              w={["full", "40%"]}
              gap={4}
            >
              <VStack
                alignItems={["flex-start", "center"]}
                h="full"
                justifyItems={"flex-start"}
                gap={0}
                w="50%"
              >
                <Text
                  color="gray.600"
                  fontSize={"x-small"}
                  letterSpacing={"wide"}
                  textTransform={"uppercase"}
                  fontWeight={"semibold"}
                  textAlign={["start", "center"]}
                >
                  Tickets
                </Text>
                <Text fontWeight={"semibold"} fontSize={"large"}>
                  {tt.prices.reduce((acc, curr) => acc + curr.sold, 0)}
                </Text>
              </VStack>
              <VStack
                alignItems={"flex-end"}
                h="full"
                gap={0}
                justifyItems={"flex-start"}
                w="50%"
              >
                <Text
                  color="gray.600"
                  fontSize={"x-small"}
                  letterSpacing={"wide"}
                  textTransform={"uppercase"}
                  fontWeight={"semibold"}
                  textAlign={"end"}
                >
                  Total face value
                </Text>
                <Text fontWeight={"semibold"} fontSize={"large"}>
                  {formatCurrency(
                    tt.prices.reduce((acc, curr) => acc + curr.gross.amount, 0),
                    tt.prices[0]?.gross.currency
                  )}
                </Text>
              </VStack>
            </Stack>
          </Stack>
          <VStack
            flexGrow={1}
            alignItems={"flex-start"}
            fontSize={"small"}
            w="full"
            gap={[4]}
          >
            {tt.prices.map((price) => (
              <HStack
                w="full"
                justifyContent={"space-between"}
                gap={[2, 8]}
                key={price.name}
              >
                <VStack
                  alignItems={"flex-start"}
                  flexGrow={1}
                  w={["full", "60%"]}
                  gap={1}
                >
                  <HStack>
                    {price.source === "waitlist" ? (
                      <Icon as={FaHourglassHalf} />
                    ) : null}
                    {price.type === "discount" ? <Icon as={FaTag} /> : null}
                    <Text color="gray.600">{price.name}</Text>
                  </HStack>
                </VStack>
                <Stack
                  direction={["row"]}
                  justifyContent={["flex-end"]}
                  w={["full", "40%"]}
                  gap={4}
                >
                  <VStack
                    h="full"
                    w="50%"
                    alignItems={["flex-start", "center"]}
                  >
                    <Text>{price.sold}</Text>
                  </VStack>
                  <VStack alignItems={"flex-end"} w="50%">
                    <Text>
                      {formatCurrency(price.gross.amount, price.gross.currency)}
                    </Text>
                  </VStack>
                </Stack>
              </HStack>
            ))}
          </VStack>
        </Stack>
      ))}
    </VStack>
  );
};

export default SettlementReportSection;
